<template>
  <Block v-model="toggleValue" :title :disabled>
    <div v-if="toggleValue" class="module-rules-form-settings-criteria">
      <div
        v-for="(param, index) in params"
        :key="index"
        class="module-rules-form-settings-criteria__item"
      >
        <Logic v-model="logic" :index :params-length="params.length" />
        <div class="module-rules-form-settings-criteria__params">
          <component
            :is="component"
            :model-value="param"
            :disabled
            @update:model-value="params[index] = $event"
          />
        </div>
        <UIButtonIcon
          v-if="params.length > 1"
          :leading="TrashIcon"
          :disabled
          variant="danger"
          fill="light"
          size="xs"
          @click="handleClickRemove(index)"
        />
      </div>
      <div :class="buttonClasses">
        <UIButton
          label="Add criteria"
          variant="secondary"
          size="sm"
          :leading="PlusIcon"
          :disabled
          @click="handleClickAdd"
        />
      </div>
    </div>
  </Block>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, ref, watch } from 'vue'

import {
  FilterLogic,
  TransactionRuleCriteria,
  TransactionRuleCriteriaType,
} from '@types'

import Block from './components/Block.vue'
import CriteriaAccountName from './components/CriteriaAccountName.vue'
import CriteriaAccountNumber from './components/CriteriaAccountNumber.vue'
import CriteriaAccountType from './components/CriteriaAccountType.vue'
import CriteriaAmount from './components/CriteriaAmount.vue'
import CriteriaCategory from './components/CriteriaCategory.vue'
import CriteriaConnector from './components/CriteriaConnector.vue'
import CriteriaMethod from './components/CriteriaMethod.vue'
import CriteriaStatus from './components/CriteriaStatus.vue'
import CriteriaText from './components/CriteriaText.vue'
import CriteriaTransactionType from './components/CriteriaTransactionType.vue'
import CriteriaTransactionDate from './components/CriteriaTransactionDate.vue'
import Logic from '@/components/UI/Filter/components/Logic.vue'

import { PlusIcon, TrashIcon } from '@heroicons/vue/24/outline'
import { UIButton } from '@ui/buttons'
import { UIButtonIcon } from '@ui/buttons'

type Props = {
  name: TransactionRuleCriteriaType
  index: number
  prevCriteria: TransactionRuleCriteria[]
  disabled?: boolean
}

const props = defineProps<Props>()

const modelValue = defineModel<TransactionRuleCriteria[]>({
  required: true,
})

const toggleValue = ref(false)

const title = computed(
  () => `${props.name.charAt(0).toUpperCase()}${props.name.slice(1)}`,
)

const modelIndex = computed(() =>
  modelValue.value.findIndex(item => item.type === props.name),
)

const buttonClasses = computed(() => ({
  'module-rules-form-settings-criteria__button--shifted':
    params.value.length > 1,
}))

const logic = computed({
  get: () => modelValue.value[modelIndex.value].logic,
  set: logic => {
    modelValue.value = modelValue.value.map((item, index) =>
      index === modelIndex.value
        ? {
          ...item,
          logic,
        }
        : item,
    )
  },
})

const params = computed({
  get: () => modelValue.value[modelIndex.value].params,
  set: params => {
    modelValue.value = modelValue.value.map((item, index) =>
      index === modelIndex.value
        ? {
          ...item,
          params,
        }
        : item,
    )
  },
})

const component = computed(() => {
  switch (props.name) {
    case TransactionRuleCriteriaType.CONNECTOR:
      return CriteriaConnector
    case TransactionRuleCriteriaType.ACCESS_METHOD:
      return CriteriaMethod
    case TransactionRuleCriteriaType.ACCOUNT_TYPE:
      return CriteriaAccountType
    case TransactionRuleCriteriaType.ACCOUNT_NAME:
      return CriteriaAccountName
    case TransactionRuleCriteriaType.ACCOUNT_NUMBER:
      return CriteriaAccountNumber
    case TransactionRuleCriteriaType.AMOUNT:
      return CriteriaAmount
    case TransactionRuleCriteriaType.CATEGORY:
      return CriteriaCategory
    case TransactionRuleCriteriaType.STATUS:
      return CriteriaStatus
    case TransactionRuleCriteriaType.TRANSACTION_TYPE:
      return CriteriaTransactionType
    case TransactionRuleCriteriaType.TRANSACTION_DATE:
      return CriteriaTransactionDate
    default:
      return CriteriaText
  }
})

const handleClickAdd = () => {
  params.value.push({ comparison: undefined, value: undefined })
}

const handleClickRemove = (index: number) => {
  params.value.splice(index, 1)
}

watch(toggleValue, value => {
  if (value) {
    const prevCriteriaValue = props.prevCriteria.find(item => item.type === props.name)
    if (prevCriteriaValue) {
      modelValue.value = [
        ...modelValue.value,
        prevCriteriaValue,
      ]
    } else {
      modelValue.value = [
        ...modelValue.value,
        {
          logic: FilterLogic.AND,
          type: props.name,
          params: [
            {
              comparison: undefined,
              value: undefined,
            },
          ],
        },
      ]
    }
  } else {
    modelValue.value = modelValue.value.filter(
      (_, index) => index !== modelIndex.value,
    )
  }
})

onBeforeMount(() => {
  toggleValue.value = modelIndex.value !== -1
})
</script>

<style>
.module-rules-form-settings-criteria {
  @apply flex flex-col gap-2;

  &__custom-field {
    @apply mb-3;
  }

  &__item {
    @apply flex items-center gap-2;
  }

  &__params {
    @apply flex flex-auto flex-col gap-1;
  }

  &__button {
    &--shifted {
      @apply ml-[4.5rem];
    }
  }
}
</style>
